/**
 * Premium Avatar Definitions
 *
 * Store-only avatars: curated memes (static) and animated avatars. Assets
 * live under /public/avatars/premium/ and are referenced by `assetPath`.
 * Ownership + equip state live in StoreSettings (ownedPremiumAvatarIds +
 * equippedPremiumAvatarId), and the active avatar is signalled on the user
 * profile via customAvatarType === 'premium' with customAvatarValue === id.
 *
 * v1 assets are hand-authored SVGs (with SMIL animations for the animated
 * tier). Replace any of them by dropping a richer GIF / WebP / PNG into the
 * same path and updating `assetPath` to match the extension.
 *
 * See STORE_SYSTEM.md §4.3.
 */

import type { Rarity, Acquisition } from './rarity';

export type PremiumAvatarKind = 'meme' | 'animated';

export interface PremiumAvatar {
  id: string;
  name: string;
  description: string;
  /** Box dust/refund value — premium avatars are earned-only, never purchased. */
  price: number;
  rarity: Rarity;
  /** Always 'earned' for premium avatars (challenge rewards / box drops). */
  acquisition?: Acquisition;
  kind: PremiumAvatarKind;
  assetPath: string;
}

// All premium avatars are earned-only (challenge rewards / surprise-box drops).
// `price` is retained solely as the box dust/refund value, never a buy price.
export const PREMIUM_AVATARS: PremiumAvatar[] = [
  // ── Memes (static SVG) ───────────────────────────────────────────────
  {
    id: 'meme-pixel-knight',
    name: 'Pixel Knight',
    description: 'Retro 8-bit armor for the keyboard battlefield.',
    price: 300,
    rarity: 'rare',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/pixel-knight.svg',
  },
  {
    id: 'meme-smug-cat',
    name: 'Smug Cat',
    description: 'Knows the answer. Refuses to share it.',
    price: 250,
    rarity: 'rare',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/smug-cat.svg',
  },
  {
    id: 'meme-terminal-face',
    name: 'Terminal Face',
    description: 'Lives inside the shell. Blinks like a cursor.',
    price: 400,
    rarity: 'epic',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/terminal-face.svg',
  },
  {
    id: 'meme-neon-wraith',
    name: 'Neon Wraith',
    description: 'Hooded silhouette. Cyberpunk visor. No mercy.',
    price: 400,
    rarity: 'epic',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/neon-wraith.svg',
  },

  // ── Collected classic internet memes (raster) ────────────────────────
  {
    id: 'meme-mocking-spongebob',
    name: 'Mocking SpongeBob',
    description: 'mAkEs EvErYtHiNg yOu SaY sOuNd RiDiCuLoUs.',
    price: 150,
    rarity: 'common',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/mocking-spongebob.jpg',
  },
  {
    id: 'meme-success-kid',
    name: 'Success Kid',
    description: 'Clenched fist. Nailed it on the first try.',
    price: 150,
    rarity: 'common',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/success-kid.jpg',
  },
  {
    id: 'meme-bad-luck-brian',
    name: 'Bad Luck Brian',
    description: 'Pushes to main. Deletes prod. Smiles anyway.',
    price: 150,
    rarity: 'common',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/bad-luck-brian.jpg',
  },
  {
    id: 'meme-trollface',
    name: 'Trollface',
    description: 'Problem? The original face of internet chaos.',
    price: 280,
    rarity: 'rare',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/trollface.png',
  },
  {
    id: 'meme-doge',
    name: 'Doge',
    description: 'such type. very speed. wow.',
    price: 280,
    rarity: 'rare',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/doge.jpg',
  },
  {
    id: 'meme-disaster-girl',
    name: 'Disaster Girl',
    description: 'The build is on fire. She is at peace.',
    price: 280,
    rarity: 'rare',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/disaster-girl.png',
  },
  {
    id: 'meme-feels-good-man',
    name: 'Feels Good Man',
    description: 'Green, smug, eternal. Feels good to type, man.',
    price: 420,
    rarity: 'epic',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/pepe.jpg',
  },
  {
    id: 'meme-mr-incredible',
    name: 'Uncanny Incredible',
    description: 'When the WPM was fine. But then it was not.',
    price: 420,
    rarity: 'epic',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/mr-incredible.png',
  },

  // ── Collected classic memes, batch 2 (rare + epic only) ──────────────
  // Epic tier — the most iconic / coveted faces.
  {
    id: 'meme-stonks',
    name: 'Stonks',
    description: 'Line goes up. WPM goes up. Stonks.',
    price: 420,
    rarity: 'epic',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/stonks.jpg',
  },
  {
    id: 'meme-cheems',
    name: 'Cheems',
    description: 'A smol doge who lubs to typ. so fast. much wow.',
    price: 420,
    rarity: 'epic',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/cheems.jpg',
  },
  {
    id: 'meme-grumpy-cat',
    name: 'Grumpy Cat',
    description: 'You hit a new high score. Good. He hated it.',
    price: 420,
    rarity: 'epic',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/grumpycat.jpg',
  },
  {
    id: 'meme-roll-safe',
    name: 'Roll Safe',
    description: "Can't make typos if you type one letter at a time.",
    price: 420,
    rarity: 'epic',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/rollsafe.jpg',
  },
  {
    id: 'meme-harold',
    name: 'Hide the Pain Harold',
    description: 'Smiling through the third retry of the level.',
    price: 420,
    rarity: 'epic',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/harold.jpg',
  },
  {
    id: 'meme-feels-bad-man',
    name: 'Feels Bad Man',
    description: 'Missed the daily goal by one level. feelsbadman.',
    price: 420,
    rarity: 'epic',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/sadfrog.jpg',
  },
  {
    id: 'meme-salt-bae',
    name: 'Salt Bae',
    description: 'Sprinkles keystrokes with unreasonable flair.',
    price: 420,
    rarity: 'epic',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/saltbae.jpg',
  },
  {
    id: 'meme-wonka',
    name: 'Condescending Wonka',
    description: 'Oh, you type 120 WPM? Do tell me more.',
    price: 420,
    rarity: 'epic',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/wonka.jpg',
  },
  {
    id: 'meme-philosoraptor',
    name: 'Philosoraptor',
    description: 'If you type in the dark, are the typos real?',
    price: 420,
    rarity: 'epic',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/philosoraptor.jpg',
  },
  {
    id: 'meme-conspiracy-keanu',
    name: 'Conspiracy Keanu',
    description: 'What if the keyboard was typing us the whole time?',
    price: 420,
    rarity: 'epic',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/keanu.jpg',
  },
  // Rare tier.
  {
    id: 'meme-y-u-no',
    name: 'Y U NO Guy',
    description: 'Y U NO hit the streak today?!',
    price: 280,
    rarity: 'rare',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/yuno.jpg',
  },
  {
    id: 'meme-yo-dawg',
    name: 'Yo Dawg',
    description: 'Yo dawg, I put a keyboard in your keyboard.',
    price: 280,
    rarity: 'rare',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/yodawg.jpg',
  },
  {
    id: 'meme-most-interesting',
    name: 'Most Interesting Man',
    description: "I don't always type fast. But when I do, no typos.",
    price: 280,
    rarity: 'rare',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/interesting.jpg',
  },
  {
    id: 'meme-good-guy-greg',
    name: 'Good Guy Greg',
    description: 'Finishes the level. Lets you keep the high score.',
    price: 280,
    rarity: 'rare',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/ggg.jpg',
  },
  {
    id: 'meme-scumbag-steve',
    name: 'Scumbag Steve',
    description: 'Borrows your keyboard. Remaps every key.',
    price: 280,
    rarity: 'rare',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/ss.jpg',
  },
  {
    id: 'meme-futurama-fry',
    name: 'Futurama Fry',
    description: 'Not sure if fast typist… or just mashing keys.',
    price: 280,
    rarity: 'rare',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/fry.jpg',
  },
  {
    id: 'meme-ancient-aliens',
    name: 'Ancient Aliens Guy',
    description: "I'm not saying it was autocorrect, but…",
    price: 280,
    rarity: 'rare',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/aag.jpg',
  },
  {
    id: 'meme-overly-attached',
    name: 'Overly Attached Girlfriend',
    description: "You practiced today. She watched. Every key.",
    price: 280,
    rarity: 'rare',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/oag.jpg',
  },
  {
    id: 'meme-persian-cat',
    name: 'Persian Cat Guardian',
    description: 'Guards the keyboard. Permits no typing.',
    price: 280,
    rarity: 'rare',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/persian.jpg',
  },
  {
    id: 'meme-buy-a-boat',
    name: 'Buy a Boat Cat',
    description: 'I should buy a mechanical keyboard.',
    price: 280,
    rarity: 'rare',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/boat.jpg',
  },
  {
    id: 'meme-foul-bachelor-frog',
    name: 'Foul Bachelor Frog',
    description: 'Keyboard hasn’t been cleaned since the install.',
    price: 280,
    rarity: 'rare',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/fbf.jpg',
  },
  {
    id: 'meme-laughing-lizard',
    name: 'Laughing Lizard',
    description: 'Saw your typo. Will never let it go.',
    price: 280,
    rarity: 'rare',
    acquisition: 'earned',
    kind: 'meme',
    assetPath: '/avatars/premium/memes/ll.jpg',
  },

  // ── Animated (SVG + SMIL) ────────────────────────────────────────────
  {
    id: 'anim-eternal-flame',
    name: 'Eternal Flame',
    description: 'A looping flame. Your streak made visible.',
    price: 500,
    rarity: 'epic',
    acquisition: 'earned',
    kind: 'animated',
    assetPath: '/avatars/premium/animated/eternal-flame.svg',
  },
  {
    id: 'anim-crystal-spin',
    name: 'Crystal Spin',
    description: 'Rotating gem that catches every angle.',
    price: 600,
    rarity: 'epic',
    acquisition: 'earned',
    kind: 'animated',
    assetPath: '/avatars/premium/animated/crystal-spin.svg',
  },
  {
    id: 'anim-mana-orb',
    name: 'Mana Orb',
    description: 'Pulsing aura with orbiting particles. Pure magic.',
    price: 750,
    rarity: 'epic',
    acquisition: 'earned',
    kind: 'animated',
    assetPath: '/avatars/premium/animated/mana-orb.svg',
  },
  {
    id: 'anim-prism-skull',
    name: 'Prism Skull',
    description: 'RGB eye sockets. Cycles through every color. Spooky.',
    price: 1000,
    rarity: 'legendary',
    acquisition: 'earned',
    kind: 'animated',
    assetPath: '/avatars/premium/animated/prism-skull.svg',
  },
  {
    id: 'anim-lightning-strike',
    name: 'Lightning Strike',
    description: 'Crackling bolt that flashes and forks. Pure voltage.',
    price: 550,
    rarity: 'epic',
    acquisition: 'earned',
    kind: 'animated',
    assetPath: '/avatars/premium/animated/lightning-strike.svg',
  },
  {
    id: 'anim-water-vortex',
    name: 'Water Vortex',
    description: 'Concentric rings spinning around a glowing core. Hypnotic.',
    price: 600,
    rarity: 'epic',
    acquisition: 'earned',
    kind: 'animated',
    assetPath: '/avatars/premium/animated/water-vortex.svg',
  },
  {
    id: 'anim-atomic-orbit',
    name: 'Atomic Orbit',
    description: 'Three electrons racing around a glowing nucleus.',
    price: 750,
    rarity: 'epic',
    acquisition: 'earned',
    kind: 'animated',
    assetPath: '/avatars/premium/animated/atomic-orbit.svg',
  },
  {
    id: 'anim-glitch-block',
    name: 'Glitch Block',
    description: 'RGB channel-tear and scan-line artifacts. Pure digital chaos.',
    price: 650,
    rarity: 'epic',
    acquisition: 'earned',
    kind: 'animated',
    assetPath: '/avatars/premium/animated/glitch-block.svg',
  },
  {
    id: 'anim-galaxy-spiral',
    name: 'Galaxy Spiral',
    description: 'A whole galaxy rotating slowly around its pulsing core.',
    price: 900,
    rarity: 'legendary',
    acquisition: 'earned',
    kind: 'animated',
    assetPath: '/avatars/premium/animated/galaxy-spiral.svg',
  },
  {
    id: 'anim-holo-eye',
    name: 'Holo Eye',
    description: 'A cybernetic iris that dilates and tracks. Always watching.',
    price: 1100,
    rarity: 'legendary',
    acquisition: 'earned',
    kind: 'animated',
    assetPath: '/avatars/premium/animated/holo-eye.svg',
  },
];

export function getPremiumAvatar(id: string | undefined): PremiumAvatar | null {
  if (!id) return null;
  return PREMIUM_AVATARS.find(a => a.id === id) ?? null;
}

export function getPremiumAvatarsByKind(kind: PremiumAvatarKind): PremiumAvatar[] {
  return PREMIUM_AVATARS.filter(a => a.kind === kind);
}
